import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import StyledQRCode from '../components/StyledQRCode';
import { apiCall } from '../utils/api';

function ProjectDetail({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [qrcodes, setQrcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [editingQR, setEditingQR] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDestinationUrl, setEditDestinationUrl] = useState('');
  const [editCampaign, setEditCampaign] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, qrcodesRes] = await Promise.all([
        apiCall(`/api/projects/${projectId}`),
        apiCall(`/api/projects/${projectId}/qrcodes`)
      ]);

      const projectData = await projectRes.json();
      const qrcodesData = await qrcodesRes.json();

      setProject(projectData);
      setQrcodes(qrcodesData.qrcodes);
    } catch (error) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const deleteQRCode = async (qrId) => {
    if (!window.confirm('Delete this QR code?')) return;

    try {
      const response = await apiCall(`/api/qrcodes/${qrId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('QR code deleted');
        fetchProjectData();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const copyShortLink = async (shortCode, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const shortLink = `${window.location.origin}/r/${shortCode}`;
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shortLink);
        setCopiedId(shortCode);
        toast.success('Short link copied!');
        setTimeout(() => setCopiedId(null), 2000);
      } else {
        // Fallback for browsers without clipboard API
        const textarea = document.createElement('textarea');
        textarea.value = shortLink;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopiedId(shortCode);
        toast.success('Short link copied!');
        setTimeout(() => setCopiedId(null), 2000);
      }
    } catch (error) {
      // Permission denied - use textarea fallback
      const textarea = document.createElement('textarea');
      textarea.value = shortLink;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        toast.success('Short link copied!');
      } catch (e) {
        toast.error('Please copy manually');
        setTimeout(() => alert(`Copy this link:\n\n${shortLink}`), 100);
      }
      document.body.removeChild(textarea);
    }
  };

  const cloneQRCode = async (qr, e) => {
    e.stopPropagation();
    
    try {
      const newShortCode = Math.random().toString(36).substring(2, 8);
      const formData = new FormData();
      formData.append('name', `${qr.name} (Copy)`);
      formData.append('destination_url', qr.destination_url);
      formData.append('short_code', newShortCode);
      
      const utm = qr.utm || {};
      formData.append('utm_source', utm.source || '');
      formData.append('utm_medium', utm.medium || '');
      formData.append('utm_campaign', utm.campaign || '');
      
      const customization = qr.customization || {};
      formData.append('fg_color', customization.fg_color || '#000000');
      formData.append('bg_color', customization.bg_color || '#FFFFFF');
      formData.append('error_correction', customization.error_correction || 'H');
      formData.append('size', customization.size || 400);
      formData.append('pattern', customization.pattern || 'squares');
      formData.append('corner_style', customization.corner_style || 'square');
      formData.append('logo_size', customization.logo_size || 20);
      if (customization.logo_file_id) {
        formData.append('logo_file_id', customization.logo_file_id);
      }

      const response = await apiCall(`/api/projects/${projectId}/qrcodes`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('QR code cloned!');
        fetchProjectData();
      } else {
        toast.error('Failed to clone');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const downloadQR = async (qr, format, e) => {
    e.stopPropagation();
    try {
      const response = await apiCall(
        `/api/qrcodes/${qr.id}/download?format=${format}&size=${qr.customization?.size || 400}`
      );
      
      if (!response.ok) {
        toast.error(`Download failed: Error ${response.status}`);
        return;
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_${qr.short_code}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`QR downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('[Download] Error:', error);
      toast.error(`Download failed: ${error.message || 'Network error'}`);
    }
  };

  const openEditModal = (qr, e) => {
    e.stopPropagation();
    setEditingQR(qr);
    setEditName(qr.name);
    setEditDestinationUrl(qr.destination_url);
    setEditCampaign(qr.utm?.campaign || '');
  };

  const updateQR = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const formData = new FormData();
      formData.append('name', editName);
      formData.append('destination_url', editDestinationUrl);
      formData.append('utm_campaign', editCampaign);

      const response = await apiCall(`/api/qrcodes/${editingQR.id}`, {
        method: 'PATCH',
        body: formData,
      });

      if (response.ok) {
        toast.success('✅ Target URL updated! QR code redirects to new destination.');
        setEditingQR(null);
        fetchProjectData();
      } else {
        toast.error('Failed to update');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  const filteredQRCodes = qrcodes.filter(qr => {
    // Search filter - expanded to include campaign name
    const matchesSearch = searchQuery === '' || 
      qr.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      qr.destination_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
      qr.short_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (qr.utm?.campaign && qr.utm.campaign.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Status filter
    const isExpired = qr.expiration_date && new Date(qr.expiration_date) < new Date();
    const isActive = qr.is_active !== false && !isExpired;
    const matchesStatus = 
      filterStatus === 'all' ||
      (filterStatus === 'active' && isActive) ||
      (filterStatus === 'expired' && isExpired) ||
      (filterStatus === 'disabled' && qr.is_active === false);
    
    // Campaign filter
    const campaign = qr.utm?.campaign || 'no-campaign';
    const matchesCampaign = filterCampaign === 'all' || campaign === filterCampaign;
    
    return matchesSearch && matchesStatus && matchesCampaign;
  });

  // Get unique campaigns for filter
  const campaigns = [...new Set(qrcodes.map(qr => qr.utm?.campaign || 'no-campaign'))].filter(c => c !== 'no-campaign');

  return (
    <div className="app-layout">
      <Sidebar onLogout={onLogout} user={user} />
      
      <div className="main-content">
        <Topbar user={user} pageTitle={project.name} />
        
        <div className="page-content">
          {/* Breadcrumb */}
          <nav className="breadcrumb">
            <span className="breadcrumb-item" onClick={() => navigate('/projects')} style={{ cursor: 'pointer' }}>Projects</span>
            <span className="breadcrumb-item active">{project.name}</span>
          </nav>

          {/* Project Stats */}
          <div className="row mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="stat-card">
                <div className="stat-card-icon">📱</div>
                <h3>{project.qr_count}</h3>
                <p>QR Codes</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-card info">
                <div className="stat-card-icon">👁️</div>
                <h3>{project.total_scans}</h3>
                <p>Total Scans</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-card success">
                <div className="stat-card-icon">🎯</div>
                <h3>{project.qr_count > 0 ? Math.round((project.total_scans / project.qr_count) * 10) / 10 : 0}</h3>
                <p>Avg per QR</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '120px', cursor: 'auto' }}>
                <button
                  className="btn btn-success"
                  onClick={() => navigate(`/projects/${projectId}/create-qr`)}
                  data-testid="create-qr-button"
                  style={{ fontSize: '1rem' }}
                >
                  <span style={{ fontSize: '1.5rem' }}>+</span> Create QR Code
                </button>
              </div>
            </div>
          </div>

          {/* QR Codes Section */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center" style={{ paddingBottom: '1rem' }}>
              <div>
                <h5 className="card-title mb-1">QR Codes</h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                  {filteredQRCodes.length} of {qrcodes.length} QR codes
                </p>
              </div>
              
              {/* View Mode Selector */}
              <div className="d-flex gap-2 align-items-center">
                <span className="text-muted" style={{ fontSize: '0.875rem', marginRight: '0.5rem' }}>View:</span>
                <button
                  className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                  style={{ width: '36px', height: '36px', padding: '0.5rem' }}
                >
                  ▦
                </button>
                <button
                  className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                  style={{ width: '36px', height: '36px', padding: '0.5rem' }}
                >
                  ☰
                </button>
                <button
                  className={`btn btn-sm ${viewMode === 'table' ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setViewMode('table')}
                  title="Table View"
                  style={{ width: '36px', height: '36px', padding: '0.5rem' }}
                >
                  ≡
                </button>
              </div>
            </div>

            {/* Filters Row - Better Styled */}
            <div style={{ padding: '1rem 1.25rem', background: 'var(--gray-100)', borderBottom: '1px solid var(--border-color)' }}>
              <div className="row">
                <div className="col-md-5">
                  <div className="search-box">
                    <span className="search-box-icon">🔍</span>
                    <input
                      type="text"
                      className="input"
                      placeholder="Search by name, URL, campaign..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      data-testid="search-qr-input"
                      style={{ fontSize: '0.875rem' }}
                    />
                  </div>
                </div>
                
                <div className="col-md-3">
                  <select
                    className="input"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="filter-status"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value="all">📊 All Status</option>
                    <option value="active">✅ Active Only</option>
                    <option value="expired">⏰ Expired</option>
                    <option value="disabled">🚫 Disabled</option>
                  </select>
                </div>
                
                <div className="col-md-4">
                  <select
                    className="input"
                    value={filterCampaign}
                    onChange={(e) => setFilterCampaign(e.target.value)}
                    data-testid="filter-campaign"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value="all">🎯 All Campaigns</option>
                    {campaigns.map(campaign => (
                      <option key={campaign} value={campaign}>📋 {campaign}</option>
                    ))}
                    <option value="no-campaign">❌ No Campaign</option>
                  </select>
                </div>
              </div>
            </div>

            {filteredQRCodes.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📱</div>
                <h3>No QR codes found</h3>
                <p className="text-muted">
                  {searchQuery || filterStatus !== 'all' || filterCampaign !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first QR code for this project'}
                </p>
                {!searchQuery && filterStatus === 'all' && filterCampaign === 'all' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/projects/${projectId}/create-qr`)}
                  >
                    Create QR Code
                  </button>
                )}
              </div>
            ) : (
              <div>
                {/* Grid View */}
                {viewMode === 'grid' && (
                  <div className="grid grid-cols-2" style={{ padding: '1.25rem' }}>
                {filteredQRCodes.map((qr) => (
                  <div key={qr.id} className="card qr-card" data-testid={`qr-card-${qr.id}`}>
                    <div className="d-flex gap-3 mb-3">
                      <div className="qr-preview-box">
                        <StyledQRCode
                          value={`${window.location.origin}/r/${qr.short_code}`}
                          size={52}
                          fgColor={qr.customization?.fg_color || '#000000'}
                          bgColor={qr.customization?.bg_color || '#FFFFFF'}
                          pattern={qr.customization?.pattern || 'squares'}
                          cornerStyle={qr.customization?.corner_style || 'square'}
                          frameStyle="none"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h5 className="mb-1">{qr.name}</h5>
                        {qr.expiration_date && new Date(qr.expiration_date) < new Date() && (
                          <span className="badge badge-danger mb-1">EXPIRED</span>
                        )}
                        {qr.expiration_date && new Date(qr.expiration_date) >= new Date() && (
                          <span className="badge badge-warning mb-1">Expires: {new Date(qr.expiration_date).toLocaleDateString()}</span>
                        )}
                        {!qr.is_active && (
                          <span className="badge badge-danger mb-1">DISABLED</span>
                        )}
                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                          /r/{qr.short_code}
                        </p>
                        <p
                          className="text-muted mb-0"
                          style={{
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {qr.destination_url}
                        </p>
                      </div>
                    </div>

                    {/* Short Link Box */}
                    <div className="short-link-box mb-3">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>Short Link</p>
                        <p className="mb-0" style={{ fontSize: '0.75rem', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {window.location.origin}/r/{qr.short_code}
                        </p>
                      </div>
                      <button
                        className={`btn btn-sm ${copiedId === qr.short_code ? 'btn-success' : 'btn-light'}`}
                        onClick={(e) => copyShortLink(qr.short_code, e)}
                        data-testid={`copy-link-${qr.id}`}
                        aria-label="Copy short link"
                      >
                        {copiedId === qr.short_code ? '✓' : '📋'}
                      </button>
                    </div>

                    {/* Stats */}
                    <div className="d-flex gap-3 mb-3">
                      <div>
                        <h4 style={{ color: 'var(--success)', marginBottom: '0' }}>{qr.scan_count || 0}</h4>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Scans</p>
                      </div>
                      {qr.last_scanned && (
                        <div>
                          <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Last scanned</p>
                          <p className="mb-0" style={{ fontSize: '0.75rem' }}>
                            {new Date(qr.last_scanned).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-2 mb-2">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={(e) => openEditModal(qr, e)}
                        data-testid={`edit-qr-${qr.id}`}
                        style={{ flex: 1 }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        onClick={() => navigate(`/qr/${qr.id}`)}
                        data-testid={`view-qr-${qr.id}`}
                      >
                        📊 Analytics
                      </button>
                    </div>
                    
                    {/* Download & Action Buttons */}
                    <div className="d-flex gap-2 mb-2">
                      <button
                        className="btn btn-light btn-sm"
                        style={{ flex: 1 }}
                        onClick={(e) => downloadQR(qr, 'png', e)}
                        data-testid={`download-png-${qr.id}`}
                      >
                        📥 PNG
                      </button>
                      <button
                        className="btn btn-light btn-sm"
                        style={{ flex: 1 }}
                        onClick={(e) => downloadQR(qr, 'svg', e)}
                        data-testid={`download-svg-${qr.id}`}
                      >
                        📥 SVG
                      </button>
                    </div>
                    
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-light btn-sm"
                        onClick={(e) => cloneQRCode(qr, e)}
                        data-testid={`clone-qr-${qr.id}`}
                        title="Clone"
                      >
                        📋
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteQRCode(qr.id)}
                        data-testid={`delete-qr-${qr.id}`}
                      >
                        🗑️
                      </button>
                    </div>
                    
                    {/* Download Buttons */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-light btn-sm"
                        style={{ flex: 1 }}
                        onClick={(e) => downloadQR(qr, 'png', e)}
                        data-testid={`download-png-${qr.id}`}
                      >
                        ⬇️ PNG
                      </button>
                      <button
                        className="btn btn-light btn-sm"
                        style={{ flex: 1 }}
                        onClick={(e) => downloadQR(qr, 'svg', e)}
                        data-testid={`download-svg-${qr.id}`}
                      >
                        ⬇️ SVG
                      </button>
                    </div>
                  </div>
                ))}
                  </div>
                )}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && filteredQRCodes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '1.25rem' }}>
                {filteredQRCodes.map((qr) => {
                  const isExpired = qr.expiration_date && new Date(qr.expiration_date) < new Date();
                  const isActive = qr.is_active !== false && !isExpired;
                  
                  return (
                    <div key={qr.id} className="card" style={{ padding: '1rem' }}>
                      <div className="d-flex gap-3 align-items-center">
                        <div className="qr-preview-box">
                          <StyledQRCode
                            value={`${window.location.origin}/r/${qr.short_code}`}
                            size={52}
                            fgColor={qr.customization?.fg_color || '#000000'}
                            bgColor={qr.customization?.bg_color || '#FFFFFF'}
                            pattern={qr.customization?.pattern || 'squares'}
                            cornerStyle={qr.customization?.corner_style || 'square'}
                            frameStyle="none"
                          />
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <h5 className="mb-1">{qr.name}</h5>
                          <p className="text-muted mb-1" style={{ fontSize: '0.875rem' }}>{qr.destination_url}</p>
                          <div className="d-flex gap-2" style={{ flexWrap: 'wrap' }}>
                            <span className="badge badge-primary" style={{ fontSize: '0.7rem' }}>
                              /r/{qr.short_code}
                            </span>
                            {qr.utm?.campaign && (
                              <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                                Campaign: {qr.utm.campaign}
                              </span>
                            )}
                            {isExpired && <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>EXPIRED</span>}
                            {!isActive && !isExpired && <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>DISABLED</span>}
                          </div>
                        </div>
                        
                        <div className="d-flex gap-3">
                          <div className="text-center">
                            <h4 style={{ color: 'var(--success)', marginBottom: 0 }}>{qr.scan_count || 0}</h4>
                            <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Scans</p>
                          </div>
                        </div>
                        
                        <div className="d-flex gap-2">
                          <button className="btn btn-primary btn-sm" onClick={() => navigate(`/qr/${qr.id}`)}>
                            📊 Analytics
                          </button>
                          <button className="btn btn-light btn-sm" onClick={(e) => copyShortLink(qr.short_code, e)}>
                            {copiedId === qr.short_code ? '✓' : '📋'}
                          </button>
                          <button className="btn btn-light btn-sm" onClick={(e) => downloadQR(qr, 'png', e)}>⬇️</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteQRCode(qr.id)}>🗑️</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && filteredQRCodes.length > 0 && (
              <div style={{ overflowX: 'auto', padding: '0' }}>
                <table style={{ marginBottom: 0 }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-100)' }}>
                      <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>QR Code</th>
                      <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Destination</th>
                      <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Campaign</th>
                      <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Scans</th>
                      <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                      <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Created</th>
                      <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQRCodes.map((qr) => {
                      const isExpired = qr.expiration_date && new Date(qr.expiration_date) < new Date();
                      const isActive = qr.is_active !== false && !isExpired;
                      
                      return (
                        <tr key={qr.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '0.75rem' }}>
                            <div className="d-flex align-items-center gap-2">
                              <div style={{ width: '40px', height: '40px', flexShrink: 0, background: 'white', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ fontSize: '1.5rem' }}>📱</div>
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <strong style={{ fontSize: '0.875rem', display: 'block' }}>{qr.name}</strong>
                                <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'var(--gray-600)' }}>
                                  /r/{qr.short_code}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem', maxWidth: '250px' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem', color: 'var(--body-color)' }}>
                              {qr.destination_url}
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {qr.utm?.campaign ? (
                              <span className="badge badge-info" style={{ fontSize: '0.75rem' }}>{qr.utm.campaign}</span>
                            ) : (
                              <span className="text-muted" style={{ fontSize: '0.75rem' }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <strong style={{ color: 'var(--success)', fontSize: '1.125rem' }}>{qr.scan_count || 0}</strong>
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {isExpired && <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>EXPIRED</span>}
                            {!isActive && !isExpired && <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>DISABLED</span>}
                            {isActive && <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>ACTIVE</span>}
                          </td>
                          <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                            {new Date(qr.created_at).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <div className="d-flex gap-1 justify-content-center">
                              <button className="btn btn-sm btn-success" onClick={(e) => { e.stopPropagation(); openEditModal(qr, e); }} title="Edit Target URL" style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}>✏️</button>
                              <button className="btn btn-sm btn-primary" onClick={() => navigate(`/qr/${qr.id}`)} title="Analytics" style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}>📊</button>
                              <button className="btn btn-sm btn-light" onClick={(e) => copyShortLink(qr.short_code, e)} title="Copy" style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}>{copiedId === qr.short_code ? '✓' : '📋'}</button>
                              <button className="btn btn-sm btn-light" onClick={(e) => downloadQR(qr, 'png', e)} title="Download" style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}>⬇️</button>
                              <button className="btn btn-sm btn-light" onClick={(e) => cloneQRCode(qr, e)} title="Clone" style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}>📋</button>
                              <button className="btn btn-sm btn-danger" onClick={() => deleteQRCode(qr.id)} title="Delete" style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit QR Modal */}
      {editingQR && (
        <div className="modal-overlay" onClick={() => setEditingQR(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h5 className="modal-title">✏️ Edit QR Code - Dynamic Update</h5>
              <button
                onClick={() => setEditingQR(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--gray-600)' }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="alert alert-info mb-3">
                <strong>🔄 Dynamic QR Code</strong><br/>
                Update the target URL and the QR code will redirect to the new destination - no need to reprint!
              </div>

              <form onSubmit={updateQR} id="edit-qr-form">
                <div className="mb-3">
                  <label className="label">QR Code Name</label>
                  <input
                    type="text"
                    className="input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Internal name for this QR"
                  />
                </div>

                <div className="mb-3">
                  <label className="label">🎯 Target URL (Destination) *</label>
                  <input
                    type="url"
                    className="input"
                    value={editDestinationUrl}
                    onChange={(e) => setEditDestinationUrl(e.target.value)}
                    required
                    placeholder="https://sparkqr.app/new-page"
                  />
                  <p className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                    This is where users will be redirected when they scan the QR code
                  </p>
                </div>

                <div className="mb-3">
                  <label className="label">📋 Campaign Name</label>
                  <input
                    type="text"
                    className="input"
                    value={editCampaign}
                    onChange={(e) => setEditCampaign(e.target.value)}
                    placeholder="e.g., winter_2025, black_friday"
                  />
                  <p className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                    Used for UTM tracking and filtering QR codes
                  </p>
                </div>

                <div className="alert alert-warning" style={{ fontSize: '0.875rem' }}>
                  <strong>ℹ️ Note:</strong> The QR code image and short link (/r/{editingQR.short_code}) remain the same. Only the redirect destination changes.
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setEditingQR(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="edit-qr-form"
                className="btn btn-success"
                disabled={updating}
              >
                {updating ? 'Updating...' : '✅ Update Target URL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDetail;
